import { useMemo } from "react";
import CustomModal from "@/components/modal/CustomModal";
import {
  MIN_ASSIGNABLE_GRADES_COUNT,
  type GradeShiftCounts,
  type ShiftType,
} from "@utils/grade-util";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { Pen, Trash2Icon } from "lucide-react";
import { Input } from "@/components/ui/input";
import ShiftCode from "@/components/shift/ShiftCode";

export type GradeTabRow = {
  id: string;
  label: string;
  count: number;
};

/** default_shift(D/E/N/M) ↔ UI는 shift_id + 색, 데이터 키는 constraintKey */
export type GradeShiftGridRowDisplay = {
  constraintKey: ShiftType;
  displayId: string;
  color: string;
};

type GradeShiftConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  displayGrades: GradeTabRow[];
  shiftSettings: Record<string, GradeShiftCounts>;
  shiftDisplayInfo: GradeShiftGridRowDisplay[];
  totalStaffCount: number;
  gradeConfiguredTotal: number;
  /** 미지정 간호사 수 — 상단 요약 표시 */
  unassignedCount?: number;
  /** 인원 미달 시 열 헤더·셀 숫자만 destructive (툴팁 없음) */
  isInvalidGrade: (gradeId: string) => boolean;
  onMinStaffChange: (gradeId: string, shift: ShiftType, value: string) => void;
  onRenameGrade?: (gradeId: string, currentLabel: string) => void;
  onDeleteGrade?: (gradeId: string) => void;
  /** Grade 추가 (`GradeTabs` 등과 동일하게 상한 두는 경우 부모에서 `disabled` 처리) */
  onAddGrade?: () => void;
  addGradeDisabled?: boolean;
};

export function GradeShiftConfigModal({
  isOpen,
  onClose,
  onConfirm,
  displayGrades,
  shiftSettings,
  shiftDisplayInfo,
  totalStaffCount,
  gradeConfiguredTotal,
  unassignedCount = 0,
  isInvalidGrade,
  onMinStaffChange,
  onRenameGrade,
  onDeleteGrade,
  onAddGrade,
  addGradeDisabled = false,
}: GradeShiftConfigModalProps) {
  const actualGrades = useMemo(
    () => displayGrades.filter((g) => g.id !== "total"),
    [displayGrades]
  );

  /** 기본 Grade 3개까지는 삭제 버튼 미노출 */
  const showDeleteGradeButton =
    actualGrades.length > MIN_ASSIGNABLE_GRADES_COUNT && Boolean(onDeleteGrade);

  const colMinPx =
    actualGrades.length <= 5
      ? 108
      : Math.max(92, 108 - (actualGrades.length - 5) * 4);

  if (!isOpen) return null;

  return (
    <CustomModal.Panel
      onClose={onClose}
      className="w-fit  max-w-[96vw] px-5 min-w-[550px] bg-background "
      button
      btnCancelText="닫기"
      btnSuccessText="저장"
      btnCancelOnClick={onClose}
      btnSuccessOnClick={onConfirm}
    >
      <div>
        <h2 className="font-pretendard text-2xl mb-3 text-sig text-center font-semibold">
          Shift 인원 설정
        </h2>
      </div>
      <div className="flex items-center gap-5 border-b-2  mb-10  pb-3 justify-between!">
        <div className="text-center flex gap-1  text-sm font-pretendard text-muted-foreground ">
          <div>전체인원 {totalStaffCount}명</div>
          <span className="opacity-50">/</span>
          <div>
            Grade 총 설정 인원{" "}
            <span className="text-sig font-semibold">
              {gradeConfiguredTotal}
            </span>
            명
          </div>
          <span className="opacity-50">/</span>
          <div>미 지정 {unassignedCount}명 </div>
        </div>
        <Button
          type="button"
          className="ml-auto "
          variant={"outline"}
          disabled={addGradeDisabled || !onAddGrade}
          onClick={() => onAddGrade?.()}
        >
          + 추가
        </Button>
      </div>

      <div className="flex flex-col mb-5">
        <div className="flex justify-center items-start">
          <div
            className="min-w-0 "
            style={{
              width: `${36 + actualGrades.length * colMinPx}px`,
            }}
          >
            <table className="w-full table-fixed border-collapse">
              <colgroup>
                <col style={{ width: 36 }} />
                {actualGrades.map((g) => (
                  <col key={g.id} style={{ width: colMinPx }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className=" bg-background p-0" />
                  {actualGrades.map((grade, idx) => {
                    const isError = isInvalidGrade(grade.id);
                    return (
                      <th
                        key={grade.id}
                        scope="col"
                        className={cn(
                          "min-w-0 overflow-visible text-center align-middle font-normal transition-colors",
                          isError && "text-destructive"
                        )}
                      >
                        <div
                          className={cn(
                            "min-w-0 w-full border-3 border-border group translate-x-[-.5px] relative bg-muted/40 hover:bg-muted px-2 py-2 rounded-t-lg",
                            idx === actualGrades.length - 1
                              ? "border-r-3"
                              : "border-r-0"
                          )}
                        >
                          <div className="pointer-events-none absolute left-1/2 top-0 z-10 flex -translate-x-1/2 gap-[3px] p-2 opacity-0 transition-all group-hover:pointer-events-auto group-hover:top-[-40px] group-hover:opacity-100">
                            <button
                              type="button"
                              className="flex size-7  shrink-0 cursor-pointer items-center justify-center rounded-full border border-muted-foreground/50 text-muted-foreground/50 transition-colors hover:border-muted-foreground/80 hover:text-muted-foreground/80"
                              aria-label={`${grade.label} 이름 수정`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRenameGrade?.(grade.id, grade.label);
                              }}
                            >
                              <Pen className="size-4 shrink-0" />
                            </button>
                            {showDeleteGradeButton ? (
                              <button
                                type="button"
                                className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full border border-muted-foreground/50 text-muted-foreground/50 transition-colors hover:border-muted-foreground/80 hover:text-muted-foreground/80"
                                aria-label={`${grade.label} 삭제`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteGrade?.(grade.id);
                                }}
                              >
                                <Trash2Icon className="size-4 shrink-0" />
                              </button>
                            ) : null}
                          </div>

                          <div
                            className={cn(
                              "min-w-0 truncate font-pretendard text-sm font-bold mb-0.5",
                              isError ? "text-destructive" : "text-foreground"
                            )}
                            title={grade.label}
                          >
                            {grade.label}
                          </div>
                          <div
                            className={cn(
                              "font-pretendard text-[12px] leading-[17px]",
                              isError
                                ? "font-medium text-destructive"
                                : "text-muted-foreground"
                            )}
                          >
                            {grade.count}명
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {shiftDisplayInfo.map((shiftInfo) => (
                  <tr key={shiftInfo.constraintKey}>
                    <td className="min-w-0 break-all border-border text-center align-middle font-pretendard text-base font-bold leading-tight text-white">
                      <ShiftCode
                        code={shiftInfo.displayId}
                        color={shiftInfo.color}
                      />
                    </td>
                    {actualGrades.map((grade) => {
                      const isError = isInvalidGrade(grade.id);
                      return (
                        <td
                          key={grade.id}
                          className="border border-border bg-background text-center align-middle transition-colors has-[:focus]:bg-muted/40"
                          onClick={(e) => {
                            const input =
                              e.currentTarget.querySelector("input");
                            input?.focus();
                          }}
                        >
                          <div className="flex items-center justify-center gap-1 py-2">
                            <Input
                              max={99}
                              value={
                                shiftSettings[grade.id]?.[
                                  shiftInfo.constraintKey
                                ] ?? 0
                              }
                              onChange={(e) => {
                                const value = e.target.value.replace(
                                  /[^0-9]/g,
                                  ""
                                );
                                onMinStaffChange(
                                  grade.id,
                                  shiftInfo.constraintKey,
                                  value
                                );
                              }}
                              className={cn(
                                "h-8 w-10",
                                isError ? "text-destructive" : "text-foreground"
                              )}
                              inputMode="numeric"
                              pattern="[0-9]*"
                            />
                            <span
                              className={cn(
                                "ml-0.5 font-pretendard text-[13px]",
                                isError
                                  ? "text-destructive"
                                  : "text-muted-foreground"
                              )}
                            >
                              명
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </CustomModal.Panel>
  );
}
